import { useEffect, useState, useRef } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { QrCode, Camera, CheckCircle2, XCircle, User } from "lucide-react";

export default function AdminGymScanner() {
  const [members, setMembers] = useState([]);
  const [result, setResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [manualToken, setManualToken] = useState("");
  const scannerRef = useRef(null);

  useEffect(() => {
    api.get("/admin/gym/members").then((r) => setMembers(r.data));
  }, []);

  const verify = async (token) => {
    try {
      const { data } = await api.post("/gym/verify", { token });
      setResult(data);
      if (data.valid) toast.success(`✓ ${data.member.user_name} · ${data.member.plan}`);
      else toast.error(data.reason);
      api.get("/admin/gym/members").then((r) => setMembers(r.data));
    } catch (e) { toast.error(apiError(e)); }
  };

  const startScan = async () => {
    setScanning(true);
    // Give React one frame so `#qr-reader-gym` exists and is laid-out with real width.
    // Without this, html5-qrcode measures a 0-px container on mobile → blank video.
    await new Promise((r) => setTimeout(r, 120));
    try {
      // Kill any zombie instance first (previous run that didn't clean up).
      if (scannerRef.current) {
        try { await scannerRef.current.stop(); } catch { /* ignore */ }
        try { await scannerRef.current.clear(); } catch { /* ignore */ }
        scannerRef.current = null;
      }
      const html5QrCode = new Html5Qrcode("qr-reader-gym", { verbose: false });
      scannerRef.current = html5QrCode;

      // Compute qrbox as ~75% of container's smaller side.
      const el = document.getElementById("qr-reader-gym");
      const side = el ? Math.min(el.clientWidth, el.clientHeight, 320) : 240;
      const box = Math.max(160, Math.floor(side * 0.75));
      const config = { fps: 10, qrbox: { width: box, height: box }, aspectRatio: 1.0 };

      const onScan = async (decodedText) => {
        try { await html5QrCode.stop(); } catch { /* ignore */ }
        setScanning(false);
        verify(decodedText);
      };

      // Try rear camera; fall back through facingMode variants then enumerated devices.
      try {
        await html5QrCode.start({ facingMode: { exact: "environment" } }, config, onScan, () => {});
      } catch {
        try {
          await html5QrCode.start({ facingMode: "environment" }, config, onScan, () => {});
        } catch {
          const cams = await Html5Qrcode.getCameras();
          if (!cams || cams.length === 0) throw new Error("No cameras found");
          const back = cams.find((c) => /back|rear|environment/i.test(c.label)) || cams[cams.length - 1];
          await html5QrCode.start(back.id, config, onScan, () => {});
        }
      }
    } catch (e) {
      console.warn("QR scanner start failed:", e);
      toast.error(`Camera unavailable · ${e?.message || "check browser permissions"}. Enter token manually.`);
      setScanning(false);
    }
  };

  const stopScan = async () => {
    try { await scannerRef.current?.stop(); } catch { /* ignore */ }
    try { await scannerRef.current?.clear(); } catch { /* ignore */ }
    scannerRef.current = null;
    setScanning(false);
  };

  // Cleanup on unmount so the camera light doesn't stay on when navigating away.
  useEffect(() => {
    return () => {
      const s = scannerRef.current;
      if (s) {
        s.stop().catch(() => {}).finally(() => { try { s.clear(); } catch { /* ignore */ } });
      }
    };
  }, []);

  return (
    <div data-testid="admin-gym-scanner">
      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Gym &amp; Swim · QR Check-In Scanner</div>
      <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none mb-8">Fitness Scanner.</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-white/12 bg-[#131A2B] p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-mono text-xs uppercase tracking-widest text-white/50">Camera scanner</div>
            {!scanning ? (
              <button onClick={startScan} data-testid="start-scan" className="bg-brand-blue text-white px-4 py-2 text-xs uppercase font-bold tracking-widest flex items-center gap-2 hover:glow-blue">
                <Camera size={14} /> Start Scan
              </button>
            ) : (
              <button onClick={stopScan} data-testid="stop-scan" className="border border-red-400 text-red-400 px-4 py-2 text-xs uppercase font-bold tracking-widest">
                Stop
              </button>
            )}
          </div>
          <div className="relative w-full aspect-square bg-black/40 border border-white/10 overflow-hidden">
            {/* html5-qrcode injects the <video> here. Absolute-positioned so it fills the square. */}
            <div id="qr-reader-gym" className="absolute inset-0" />
            {!scanning && (
              <div className="absolute inset-0 grid place-items-center pointer-events-none">
                <QrCode size={64} className="text-white/15" />
              </div>
            )}
          </div>

          <div className="mt-4 border-t border-white/12 pt-4">
            <div className="font-mono text-xs uppercase tracking-widest text-white/50 mb-2">Manual verify (paste token)</div>
            <div className="flex gap-2">
              <input value={manualToken} onChange={(e) => setManualToken(e.target.value)} placeholder="QR token" data-testid="manual-token" className="flex-1 bg-transparent border border-white/20 py-2 px-3 font-mono text-sm" />
              <button onClick={() => verify(manualToken)} data-testid="manual-verify" className="bg-brand-orange text-white px-4 py-2 text-xs uppercase font-bold tracking-widest hover:glow-orange">Verify</button>
            </div>
          </div>
        </div>

        <div className="border border-white/12 bg-[#131A2B] p-6" data-testid="verify-result">
          <div className="font-mono text-xs uppercase tracking-widest text-white/50 mb-4">Latest verification</div>
          {result ? (
            result.valid ? (
              <div>
                <CheckCircle2 size={48} className="brand-blue mb-3" />
                <div className="font-display text-3xl uppercase font-black">Access Granted</div>
                <div className={`inline-block mt-2 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${result.kind === "swim" ? "bg-cyan-500/20 text-cyan-300" : "bg-orange-500/20 text-orange-300"}`}>
                  {result.kind === "swim" ? "🏊 Swim membership" : "💪 Gym membership"}
                </div>
                <div className="mt-4 space-y-1 text-sm">
                  <div>Member: <span className="font-bold">{result.member.user_name}</span></div>
                  <div>Plan: <span className="brand-orange uppercase">{result.member.plan}</span></div>
                  <div>Valid until: <span className="font-mono">{result.member.end_date}</span></div>
                  <div>Check-ins today: <span className="brand-blue font-bold">{result.member.check_ins?.length}</span></div>
                </div>
              </div>
            ) : (
              <div>
                <XCircle size={48} className="text-red-400 mb-3" />
                <div className="font-display text-3xl uppercase font-black text-red-400">Denied</div>
                <div className="mt-4 text-sm text-white/70">{result.reason}</div>
              </div>
            )
          ) : (
            <div className="text-white/40 text-sm">Scan or paste a token to verify.</div>
          )}
        </div>
      </div>

      <h2 className="font-display text-3xl uppercase font-black mt-16 mb-4">Active Memberships</h2>
      <div className="border border-white/12 bg-[#131A2B] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="font-mono text-xs uppercase tracking-widest text-white/40 border-b border-white/12">
              <th className="text-left p-4">Member</th>
              <th className="text-left p-4">Plan</th>
              <th className="text-left p-4">Valid Until</th>
              <th className="text-right p-4">Check-ins</th>
              <th className="text-left p-4">QR Token</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} className="border-b border-white/5" data-testid={`member-${m.id}`}>
                <td className="p-4"><User size={14} className="inline mr-2 text-white/40" />{m.user_name}</td>
                <td className="p-4 uppercase brand-orange">{m.plan}</td>
                <td className="p-4 font-mono text-xs">{m.end_date}</td>
                <td className="p-4 text-right brand-blue font-bold">{m.check_ins?.length || 0}</td>
                <td className="p-4 font-mono text-xs text-white/50">{m.qr_token?.slice(0, 12)}…</td>
              </tr>
            ))}
            {members.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-white/40">No members yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
